// Maps failed generation and preview results to the error the hero surfaces.

import type { GenerateAiDraftFromSocialPostErrorReason } from './useGenerateSocialPostDraft/api/api.graphql';
import type { SocialPostPreviewQuery } from './useSocialPostPreview/api/api.graphql';

export type SocialPostErrorCode = 'unsupported_platform' | 'invalid_link' | 'video_unavailable' | 'generic_error';

export type PreviewSuccess = Extract<
	SocialPostPreviewQuery['socialPostPreview'],
	{ __typename: 'SocialPostPreviewSuccess' }
>;

// "We couldn't build a petition from that video" covers every case where we reached the
// video but couldn't turn it into a draft: unavailable/private (SOCIAL_POST_UNAVAILABLE),
// flagged content (FLAGGED_CONTENT), or the model returning no usable draft, e.g. too little
// content (INVALID_JSON_RESPONSE). Genuine transport/HTTP failures fall through to the
// generic "give it another try" message, which is the only retryable-framed one.
const VIDEO_UNAVAILABLE_REASONS: ReadonlySet<string> = new Set<GenerateAiDraftFromSocialPostErrorReason>([
	'SOCIAL_POST_UNAVAILABLE',
	'FLAGGED_CONTENT',
	'INVALID_JSON_RESPONSE',
]);

export function mapReasonToErrorCode(reason: string | undefined): SocialPostErrorCode {
	return reason !== undefined && VIDEO_UNAVAILABLE_REASONS.has(reason) ? 'video_unavailable' : 'generic_error';
}

export function getPreviewSuccess(
	preview: SocialPostPreviewQuery['socialPostPreview'] | undefined,
): PreviewSuccess | undefined {
	return preview?.__typename === 'SocialPostPreviewSuccess' ? preview : undefined;
}

/**
 * Error code for a failed preview. UNSUPPORTED_PLATFORM means the server's allowlist is behind
 * the client's (or a multi-source arm is live before the middle-end accepts other sources).
 */
export function mapPreviewErrorToCode(
	preview: SocialPostPreviewQuery['socialPostPreview'] | undefined,
): SocialPostErrorCode {
	if (preview?.__typename === 'SocialPostPreviewError') {
		return preview.reason === 'UNSUPPORTED_PLATFORM' ? 'unsupported_platform' : 'video_unavailable';
	}
	return 'generic_error';
}
